import { Category } from "@/dto/category";
import { GetAllItemsResponse } from "@/dataLayer/getAllItems";
import { Menu } from "@/dto/menu";

export interface CategoryItemsData {
  elements: any[];
  currentPage: number;
  totalPages: number;
  totalItemCount: number;
}

interface AllItemsCacheEntry {
  params: string;
  data: GetAllItemsResponse;
}

interface MenuCacheEntry {
  params: string;
  menu: Menu;
}

export type cacheKey =
  | "categoryitems"
  | "allItems"
  | "retailItems"
  | "serviceItems"
  | "menu";

let categoryItemsCache: Map<string, CategoryItemsData> = new Map();
let allItemsCache: AllItemsCacheEntry | null = null;
let retailItemsCache: any[] | null = null;
let serviceItemsCache: Category[] | null = null;
let menuCache: MenuCacheEntry | null = null;

// retail /all page
export const getAllItemsFromCache = (
  searchParams: URLSearchParams
): GetAllItemsResponse | null => {
  if (!allItemsCache) {
    return null;
  }
  if (allItemsCache.params === searchParams.toString()) {
    return allItemsCache.data;
  }
  return null;
};

export const setAllItemsInCache = (
  data: GetAllItemsResponse | null,
  searchParams: URLSearchParams
) => {
  if (data) {
    allItemsCache = {
      params: searchParams.toString(),
      data: data,
    };
  } else {
    allItemsCache = null;
  }
};

const getCacheObjectByKey = (cacheKey: cacheKey): any => {
  switch (cacheKey) {
    case "categoryitems":
      return categoryItemsCache;
    case "allItems":
      return allItemsCache;
    case "retailItems":
      return retailItemsCache;
    case "serviceItems":
      return serviceItemsCache;
    case "menu":
      return menuCache;
    default:
      return null;
  }
};

export const areParamsMatchingCache = (
  searchParams: URLSearchParams,
  cacheKey: cacheKey = "allItems"
): boolean => {
  const cache = getCacheObjectByKey(cacheKey);
  if (!cache) {
    return false;
  }
  return cache.params === searchParams.toString();
};

// retail category pages
export const getCategoryItemsFromCache = (
  categoryId: string
): CategoryItemsData | null => {
  return categoryItemsCache.get(categoryId) || null;
};

export const setCategoryItemsInCache = (
  categoryId: string,
  data: CategoryItemsData | null
) => {
  if (data) {
    categoryItemsCache.set(categoryId, data);
  } else {
    categoryItemsCache.delete(categoryId);
  }
};

// hybrid shop tab
export const getRetailItemsFromCache = (): any[] | null => {
  return retailItemsCache;
};

export const setRetailItemsInCache = (items: any[] | null) => {
  retailItemsCache = items;
};

// hybrid services tab
export const getServiceItemsFromCache = (): Category[] | null => {
  return serviceItemsCache;
};

export const setServiceItemsInCache = (items: Category[] | null) => {
  serviceItemsCache = items;
};

// restaurant & services menu tab
export const getMenuFromCache = (
  searchParams?: URLSearchParams
): Menu | null => {
  if (!menuCache) {
    return null;
  }
  if (!searchParams) {
    return menuCache.menu;
  }
  if (menuCache.params === searchParams.toString()) {
    return menuCache.menu;
  }

  return null;
};

export const setMenuInCache = (
  menu: Menu | null,
  searchParams?: URLSearchParams
) => {
  if (menu) {
    menuCache = {
      menu,
      params: searchParams?.toString() ?? "",
    };
  } else {
    menuCache = null;
  }
};
